import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Drawer from "@material-ui/core/Drawer";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import TextField from "@material-ui/core/TextField";
import Chip from "@material-ui/core/Chip";
import Button from "@material-ui/core/Button";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import CloseIcon from "@material-ui/icons/Close";
import SendIcon from "@material-ui/icons/Send";
import RefreshIcon from "@material-ui/icons/Refresh";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { AuthContext } from "../../context/Auth/AuthContext";
import { hideMobileBar } from "../MobileBottomNav";
import { useGuide } from "../../context/Guide/GuideContext";
import api from "../../services/api";

/**
 * Digi · Ayuda de WaToolX: chat con el agente que conoce todo el manual de la plataforma.
 * El panel no bloquea la pantalla, para seguir los pasos mientras Digi guía.
 */

const SUGERENCIAS = [
  "¿Qué puedo hacer en esta pantalla?",
  "¿Cómo conecto mi WhatsApp?",
  "¿Cómo conecto mi Telegram?",
  "¿Cómo creo un departamento y asigno usuarios?",
  "¿Cómo hago una campaña masiva?",
  "¿Cómo activo el agente IA?",
  "¿Cómo publico mi tienda web?",
];

const GUIDE_NEXT = "✅ Listo, siguiente paso";
const GUIDE_LOST = "❓ No lo encuentro";
const GUIDE_STOP = "⏹ Salir del paso a paso";
const MAX_SAVED = 40;

/** Icono de Digi: robot amigable con un globo de preguntas frecuentes. */
export function DigiHelpIcon({ size = 26 }) {
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} aria-hidden="true" focusable="false">
      <line x1="12" y1="7" x2="12" y2="3.6" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="12" cy="3" r="1.7" fill="#facc15" />
      <rect x="1.5" y="13" width="2.4" height="6" rx="1.2" fill="#c7d2fe" />
      <rect x="20.1" y="13" width="2.4" height="6" rx="1.2" fill="#c7d2fe" />
      <rect x="3.2" y="7.5" width="17.6" height="17" rx="5.5" fill="#fff" />
      <rect x="5.6" y="10.6" width="12.8" height="9" rx="4" fill="#1e3a8a" />
      <g className="digi-eyes"><circle cx="9.4" cy="15" r="1.7" fill="#5eead4" /><circle cx="14.6" cy="15" r="1.7" fill="#5eead4" /></g>
      <path d="M9.6 21.6q2.4 1.6 4.8 0" fill="none" stroke="#1e3a8a" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M25 1.8a6.2 6.2 0 0 1 0 12.4h-1.1l-2.6 2.1v-2.6a6.2 6.2 0 0 1 3.7-11.9Z" fill="#22c55e" />
      <text x="25" y="11.3" textAnchor="middle" fontSize="8.6" fontWeight="800" fill="#fff" fontFamily="Arial, sans-serif">?</text>
    </svg>
  );
}

/** Altura del botón flotante según la vista: sobre la barra inferior del móvil y sobre la caja de escribir en Tickets. */
export function launcherBottom(pathname, mobile) {
  const inChat = /^\/(tickets|chats)\/.+/.test(pathname || "");
  if (mobile) return hideMobileBar(pathname || "") || inChat ? 84 : 92;
  return /^\/(tickets|chats)(\/|$)/.test(pathname || "") ? 92 : 24;
}

/** Posición del botón movida por el usuario (por dispositivo): { side: "left"|"right", bottom: px }. */
const POS_KEY = "digiLauncherPos";
const LAUNCHER_MARGIN = (mobile) => (mobile ? 14 : 20);
export function loadLauncherPos() {
  try {
    const p = JSON.parse(localStorage.getItem(POS_KEY) || "null");
    if (p && (p.side === "left" || p.side === "right") && Number.isFinite(p.bottom)) return p;
  } catch { /* sin datos */ }
  return null;
}
/** Al soltar: se pega al lado más cercano y la altura queda dentro de la pantalla. */
export function snapLauncher(left, top, size, vw, vh) {
  const side = left + size / 2 < vw / 2 ? "left" : "right";
  const bottom = Math.round(Math.min(Math.max(vh - top - size, 8), Math.max(vh - size - 8, 8)));
  return { side, bottom };
}

const useStyles = makeStyles((theme) => ({
  launcher: {
    touchAction: "none", userSelect: "none",
    position: "fixed", right: 20, zIndex: 1350, width: 60, height: 60, borderRadius: "50%", border: 0, padding: 0,
    display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
    background: "linear-gradient(145deg,#2563eb,#1e3a8a)", boxShadow: "0 10px 26px rgba(30,58,138,.45)",
    transition: "bottom .25s ease, transform .2s ease",
    animation: "$float 3.2s ease-in-out infinite",
    "&:hover": { transform: "scale(1.08)", animationPlayState: "paused" },
    "&:focus-visible": { outline: "3px solid #93c5fd", outlineOffset: 3 },
    "&::before": {
      content: '""', position: "absolute", inset: 0, borderRadius: "50%", border: "2px solid #60a5fa",
      animation: "$ring 3.2s ease-out infinite", pointerEvents: "none",
    },
    "& .digi-eyes": { transformOrigin: "12px 15px", animation: "$blink 4.5s infinite" },
    "@media (prefers-reduced-motion: reduce)": { animation: "none", "&::before": { animation: "none", opacity: 0 }, "& .digi-eyes": { animation: "none" } },
    [theme.breakpoints.down("sm")]: { right: 14, width: 54, height: 54 },
  },
  "@keyframes float": { "0%,100%": { transform: "translateY(0)" }, "50%": { transform: "translateY(-5px)" } },
  "@keyframes ring": { "0%": { transform: "scale(1)", opacity: 0.7 }, "70%,100%": { transform: "scale(1.45)", opacity: 0 } },
  "@keyframes blink": { "0%,92%,100%": { transform: "scaleY(1)" }, "95%": { transform: "scaleY(.1)" } },
  hello: {
    position: "fixed", right: 90, zIndex: 1350, maxWidth: 220, padding: "9px 12px", borderRadius: 14, borderBottomRightRadius: 4,
    background: "#fff", color: "#0f172a", fontSize: 13, fontWeight: 600, boxShadow: "0 8px 24px rgba(15,23,42,.18)",
    border: 0, cursor: "pointer", textAlign: "left", animation: "$pop .35s ease-out",
    [theme.breakpoints.down("sm")]: { right: 78 },
  },
  "@keyframes pop": { from: { opacity: 0, transform: "translateY(8px) scale(.95)" }, to: { opacity: 1, transform: "none" } },
  root: { pointerEvents: "none" },
  paper: {
    pointerEvents: "auto", width: 440, maxWidth: "100vw", display: "flex", flexDirection: "column",
    boxShadow: "-12px 0 32px rgba(15,23,42,.18)",
  },
  header: {
    display: "flex", alignItems: "center", gap: 10, padding: "12px 10px 12px 14px",
    background: "linear-gradient(135deg,#1e3a8a,#2563eb)", color: "#fff",
  },
  avatar: {
    width: 42, height: 42, borderRadius: 14, background: "rgba(255,255,255,.14)", display: "flex",
    alignItems: "center", justifyContent: "center", flex: "0 0 auto",
  },
  titleBox: { flex: 1, minWidth: 0, display: "grid" },
  title: { fontWeight: 800, fontSize: 16, lineHeight: 1.2 },
  subtitle: { fontSize: 12, opacity: 0.85 },
  tabs: { minHeight: 40, borderBottom: `1px solid ${theme.palette.divider}`, "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontWeight: 700 } },
  body: { flex: 1, overflowY: "auto", padding: 14, background: theme.palette.type === "dark" ? "#1e1e1e" : "#f5f7fb" },
  chips: { display: "flex", flexWrap: "wrap", gap: 6, margin: "4px 0 10px" },
  row: { display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 10 },
  mini: {
    width: 28, height: 28, borderRadius: 9, background: "linear-gradient(135deg,#1e3a8a,#2563eb)", display: "flex",
    alignItems: "center", justifyContent: "center", flex: "0 0 auto",
  },
  bubble: {
    padding: "10px 12px", borderRadius: 14, fontSize: 14, lineHeight: 1.55,
    whiteSpace: "pre-wrap", wordBreak: "break-word", maxWidth: "88%",
  },
  me: { marginLeft: "auto", background: "#2563eb", color: "#fff", borderBottomRightRadius: 4 },
  bot: { background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderBottomLeftRadius: 4 },
  actions: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 },
  topics: { marginTop: 6, fontSize: 11, color: theme.palette.text.secondary },
  typing: { display: "flex", gap: 4, padding: "12px 14px", "& span": { width: 7, height: 7, borderRadius: "50%", background: "#94a3b8", animation: "$dot 1.2s infinite" }, "& span:nth-child(2)": { animationDelay: ".2s" }, "& span:nth-child(3)": { animationDelay: ".4s" } },
  "@keyframes dot": { "0%,60%,100%": { opacity: 0.3 }, "30%": { opacity: 1 } },
  guide: { display: "flex", flexWrap: "wrap", gap: 6, padding: "8px 12px", borderTop: `1px solid ${theme.palette.divider}`, background: theme.palette.type === "dark" ? "#262626" : "#eef4ff" },
  footer: { display: "flex", alignItems: "flex-end", gap: 6, padding: 10, borderTop: `1px solid ${theme.palette.divider}` },
  manual: { display: "block", fontSize: 12, padding: "6px 14px 0", color: theme.palette.text.secondary },
  faqModule: { border: `1px solid ${theme.palette.divider}`, borderRadius: 12, background: theme.palette.background.paper, marginBottom: 8, overflow: "hidden" },
  faqHead: { width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "10px 12px", border: 0, background: "transparent", cursor: "pointer", font: "inherit", fontWeight: 700, color: theme.palette.text.primary, textAlign: "left" },
  faqItem: { width: "100%", display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", border: 0, borderTop: `1px solid ${theme.palette.divider}`, background: "transparent", cursor: "pointer", font: "inherit", fontSize: 13, color: theme.palette.text.primary, textAlign: "left", "&:hover": { background: theme.palette.action.hover } },
}));

/** **negrita** o *negrita*, sin HTML. */
function Rich({ text }) {
  const parts = String(text || "").split(/(\*\*[^*]+\*\*|\*[^*\n]+\*)/g);
  return parts.map((p, i) => {
    const m = p.match(/^\*\*([^*]+)\*\*$/) || p.match(/^\*([^*\n]+)\*$/);
    return m ? <strong key={i}>{m[1]}</strong> : <React.Fragment key={i}>{p.replace(/^#+ /gm, "")}</React.Fragment>;
  });
}

const CONSULTED = {
  ver_mi_usuario: "tu usuario", ver_actividad: "la actividad de tickets", buscar_en_pantallas: "las pantallas del panel", buscar_en_backend: "los mensajes del sistema",
  "ver_configuracion:conexiones": "tus conexiones", "ver_configuracion:departamentos": "tus departamentos", "ver_configuracion:usuarios": "los usuarios",
  "ver_configuracion:plan": "tu plan", "ver_configuracion:opciones": "tus opciones", "ver_configuracion:negocio": "los ajustes de Negocio",
  "ver_configuracion:agentes_ia": "tus agentes IA", "ver_configuracion:integraciones": "tus integraciones", "ver_configuracion:campanas": "tus campañas",
  "ver_configuracion:flujos": "tus flujos",
};
export const consultedLabel = (list) => [...new Set((list || []).map((c) => CONSULTED[c]).filter(Boolean))].join(", ");

const storageKey = (user) => `digiHelp:${user?.companyId || 0}:${user?.id || 0}`;
const load = (key) => {
  try { const saved = JSON.parse(localStorage.getItem(key) || "null"); return Array.isArray(saved) ? saved : []; } catch { return []; }
};

export default function ManualAssistant() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { user } = useContext(AuthContext) || {};
  const key = storageKey(user);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [turns, setTurns] = useState(() => load(key));
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [faq, setFaq] = useState(null);
  const [faqQuery, setFaqQuery] = useState("");
  const [expanded, setExpanded] = useState(null);
  const endRef = useRef(null);
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [hello, setHello] = useState(false);
  // Con una guía interactiva abierta, su panel ocupa la esquina: Digi se oculta; minimizada (píldora), Digi sube.
  const guideSession = (useGuide() || {}).session;
  const guideOpen = !!guideSession?.id && !guideSession.minimized;
  const autoBottom = launcherBottom(location.pathname, mobile) + (guideSession?.id && guideSession.minimized ? 60 : 0);

  // Arrastrable: si tapa algo (p. ej. la paginación), el usuario lo mueve y queda guardado.
  const [pos, setPos] = useState(() => loadLauncherPos());
  const [dragXY, setDragXY] = useState(null);
  const dragRef = useRef(null);
  const suppressClick = useRef(false);
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const bottom = pos ? Math.min(pos.bottom, Math.max(vh - 70, 8)) : autoBottom;
  const side = pos?.side || "right";
  const margin = LAUNCHER_MARGIN(mobile);

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    dragRef.current = { id: e.pointerId, x: e.clientX, y: e.clientY, dx: e.clientX - rect.left, dy: e.clientY - rect.top, size: rect.width, moved: false };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* sin soporte */ }
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d || d.id !== e.pointerId) return;
    if (!d.moved && Math.hypot(e.clientX - d.x, e.clientY - d.y) < 6) return;
    d.moved = true;
    const left = Math.min(Math.max(e.clientX - d.dx, 0), window.innerWidth - d.size);
    const top = Math.min(Math.max(e.clientY - d.dy, 0), window.innerHeight - d.size);
    setHello(false);
    setDragXY({ left, top });
  };
  const onPointerUp = (e) => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d || !d.moved || !dragXY) { setDragXY(null); return; }
    const next = snapLauncher(dragXY.left, dragXY.top, d.size, window.innerWidth, window.innerHeight);
    setPos(next);
    try { localStorage.setItem(POS_KEY, JSON.stringify(next)); } catch { /* sin espacio */ }
    setDragXY(null);
    suppressClick.current = true;
    setTimeout(() => { suppressClick.current = false; }, 0);
  };
  const launcherStyle = dragXY
    ? { left: dragXY.left, top: dragXY.top, right: "auto", bottom: "auto", transition: "none", animation: "none", cursor: "grabbing" }
    : { bottom: `calc(${bottom}px + env(safe-area-inset-bottom, 0px))`, ...(side === "left" ? { left: margin, right: "auto" } : {}) };
  const helloStyle = side === "left" ? { bottom: bottom + 10, left: margin + (mobile ? 64 : 70), right: "auto", borderBottomRightRadius: 14, borderBottomLeftRadius: 4 } : { bottom: bottom + 10 };
  const openFromLauncher = () => {
    if (suppressClick.current) return;
    setHello(false);
    setOpen(true);
  };

  // Saludo corto una vez por sesión del navegador, para que se note que Digi está disponible.
  useEffect(() => {
    let timer;
    try {
      if (sessionStorage.getItem("digiHello")) return undefined;
      sessionStorage.setItem("digiHello", "1");
    } catch { return undefined; }
    const show = setTimeout(() => { setHello(true); timer = setTimeout(() => setHello(false), 7000); }, 2500);
    return () => { clearTimeout(show); clearTimeout(timer); };
  }, []);

  useEffect(() => { setTurns(load(key)); }, [key]);
  useEffect(() => {
    try { localStorage.setItem(key, JSON.stringify(turns.slice(-MAX_SAVED))); } catch { /* sin espacio */ }
  }, [key, turns]);
  useEffect(() => { if (open && tab === 0) endRef.current?.scrollIntoView?.({ behavior: "smooth" }); }, [turns, busy, open, tab]);
  useEffect(() => {
    if (!open || tab !== 1 || faq) return;
    api.get("/manual-assistant/faq").then(({ data }) => setFaq(data.modules || [])).catch(() => setFaq([]));
  }, [open, tab, faq]);

  const last = turns[turns.length - 1];
  const guiding = !!(last && last.role === "assistant" && last.guided);

  const ask = async (question, opts = {}) => {
    const q = String(question || "").trim();
    if (!q || busy) return;
    const guided = opts.guided !== undefined ? opts.guided : guiding && q !== GUIDE_STOP;
    const historyTurns = turns.slice(-12).map(({ role, content }) => ({ role, content }));
    setTurns((t) => [...t, { role: "user", content: q }]);
    setText("");
    setTab(0);
    setBusy(true);
    try {
      const { data } = await api.post("/manual-assistant/ask", { question: q, history: historyTurns, guided, page: location.pathname });
      setTurns((t) => [...t, { role: "assistant", content: data.answer, temas: data.temas || [], links: data.links || [], guided: !!data.guided, consulted: data.consulted || [] }]);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || "No pude responder ahora. Inténtalo de nuevo en un momento.";
      setTurns((t) => [...t, { role: "assistant", content: msg, temas: [], links: [] }]);
    } finally {
      setBusy(false);
    }
  };

  // Otras pantallas (p. ej. Primeros pasos) piden a Digi guiar un tema: window.dispatchEvent(new CustomEvent("digi:ask", { detail: { question, guided } })).
  const askRef = useRef(ask);
  askRef.current = ask;
  useEffect(() => {
    const onAsk = (event) => {
      const question = String(event?.detail?.question || "").trim();
      if (!question) return;
      setOpen(true);
      setTab(0);
      askRef.current(question, { guided: event.detail.guided !== false });
    };
    window.addEventListener("digi:ask", onAsk);
    return () => window.removeEventListener("digi:ask", onAsk);
  }, []);

  const guide = (temas) => ask(`Guíame paso a paso: ${temas[0]?.titulo || "lo que acabas de explicar"}`, { guided: true });
  const go = (path) => { history.push(path); if (window.innerWidth < 700) setOpen(false); };

  const faqVisible = useMemo(() => {
    const q = faqQuery.trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    if (!faq) return null;
    if (!q) return faq;
    const norm = (s) => String(s).toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    return faq.map((m) => ({ ...m, temas: m.temas.filter((t) => norm(`${t.titulo} ${t.ruta} ${m.modulo}`).includes(q)) })).filter((m) => m.temas.length);
  }, [faq, faqQuery]);

  return (
    <>
      {!open && !guideOpen && createPortal(
        <>
          {hello && !dragXY && (
            <button type="button" className={classes.hello} style={helloStyle} onClick={() => { setHello(false); setOpen(true); }}>
              ¿Dudas con WaToolX? Pregúntame 👋
              <span style={{ display: "block", fontWeight: 400, fontSize: 11, color: "#64748b", marginTop: 2 }}>Si te tapo algo, arrástrame a otro lado</span>
            </button>
          )}
          <Tooltip title={dragXY ? "" : "Digi · Ayuda de WaToolX (arrástralo para moverlo)"} placement={side === "left" ? "right" : "left"} arrow>
            <button
              type="button" className={classes.launcher} style={launcherStyle}
              onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
              onClick={openFromLauncher} aria-label="Asistente de ayuda"
            >
              <DigiHelpIcon size={mobile ? 32 : 36} />
            </button>
          </Tooltip>
        </>,
        document.body
      )}
      <Drawer
        anchor="right" open={open} onClose={() => setOpen(false)}
        className={classes.root} classes={{ paper: classes.paper }} style={{ zIndex: 1400 }}
        ModalProps={{ hideBackdrop: true, disableEnforceFocus: true, disableScrollLock: true, disableAutoFocus: true }}
      >
        <div className={classes.header}>
          <div className={classes.avatar}><DigiHelpIcon size={32} /></div>
          <div className={classes.titleBox}>
            <span className={classes.title}>Digi · Ayuda de WaToolX</span>
            <span className={classes.subtitle}>Te explico cualquier función y te guío paso a paso</span>
          </div>
          <Tooltip title="Nueva conversación" arrow>
            <span>
              <IconButton size="small" onClick={() => setTurns([])} disabled={busy || !turns.length} style={{ color: "#fff" }} aria-label="Nueva conversación">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton size="small" onClick={() => setOpen(false)} style={{ color: "#fff" }} aria-label="Cerrar">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth" indicatorColor="primary" textColor="primary" className={classes.tabs}>
          <Tab label="Chat" />
          <Tab label="Preguntas frecuentes" />
        </Tabs>

        {tab === 0 ? (
          <>
            <div className={classes.body}>
              <div className={classes.row}>
                <div className={classes.mini}><DigiHelpIcon size={20} /></div>
                <div className={`${classes.bubble} ${classes.bot}`}>
                  {`¡Hola${user?.name ? `, ${String(user.name).split(" ")[0]}` : ""}! Soy Digi 👋\nPregúntame cómo hacer algo en WaToolX, qué significa una opción o por qué algo no funciona. Conozco tu plan, tus conexiones y tus departamentos, y puedo guiarte paso a paso.`}
                </div>
              </div>
              {!turns.length && (
                <div className={classes.chips}>
                  {SUGERENCIAS.map((s) => <Chip key={s} label={s} size="small" clickable onClick={() => ask(s)} />)}
                </div>
              )}
              {turns.map((t, i) => (
                <div key={i} className={classes.row}>
                  {t.role === "assistant" && <div className={classes.mini}><DigiHelpIcon size={20} /></div>}
                  <div className={`${classes.bubble} ${t.role === "user" ? classes.me : classes.bot}`}>
                    {t.role === "assistant" && consultedLabel(t.consulted) && (
                      <div className={classes.topics} style={{ marginTop: 0, marginBottom: 6 }}>🔎 Revisé {consultedLabel(t.consulted)}</div>
                    )}
                    <Rich text={t.content} />
                    {t.role === "assistant" && (t.links?.length > 0 || (t.temas?.length > 0 && !t.guided && i === turns.length - 1)) && (
                      <div className={classes.actions}>
                        {(t.links || []).map((l) => (
                          <Button key={l.path} size="small" variant="outlined" color="primary" endIcon={<ChevronRightIcon />} onClick={() => go(l.path)}>
                            Ir a {l.label}
                          </Button>
                        ))}
                        {t.temas?.length > 0 && !t.guided && i === turns.length - 1 && (
                          <Button size="small" variant="contained" color="primary" disabled={busy} onClick={() => guide(t.temas)}>
                            🧭 Guíame paso a paso
                          </Button>
                        )}
                      </div>
                    )}
                    {t.role === "assistant" && t.temas?.length > 0 && (
                      <div className={classes.topics}>Manual: {t.temas.map((x) => x.titulo).join(" · ")}</div>
                    )}
                  </div>
                </div>
              ))}
              {busy && (
                <div className={classes.row}>
                  <div className={classes.mini}><DigiHelpIcon size={20} /></div>
                  <div className={`${classes.bubble} ${classes.bot} ${classes.typing}`} aria-label="Digi está escribiendo"><span /><span /><span /></div>
                </div>
              )}
              <div ref={endRef} />
            </div>
            {guiding && !busy && (
              <div className={classes.guide}>
                {[GUIDE_NEXT, GUIDE_LOST, GUIDE_STOP].map((g) => (
                  <Chip key={g} label={g} size="small" clickable color={g === GUIDE_NEXT ? "primary" : "default"} onClick={() => ask(g, { guided: g !== GUIDE_STOP })} />
                ))}
              </div>
            )}
            <a className={classes.manual} href="/manual/Manual-Watoolx.pdf" target="_blank" rel="noopener noreferrer">
              Ver el manual completo (PDF)
            </a>
            <div className={classes.footer}>
              <TextField
                fullWidth multiline maxRows={4} size="small" variant="outlined" placeholder="Escribe tu pregunta…"
                value={text} onChange={(e) => setText(e.target.value)} disabled={busy}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(text); } }}
              />
              <IconButton color="primary" onClick={() => ask(text)} disabled={busy || !text.trim()} aria-label="Enviar">
                <SendIcon />
              </IconButton>
            </div>
          </>
        ) : (
          <div className={classes.body}>
            <TextField
              fullWidth size="small" variant="outlined" placeholder="Buscar una pregunta (ej. conexión, campaña, tienda)"
              value={faqQuery} onChange={(e) => setFaqQuery(e.target.value)} style={{ marginBottom: 10, background: "#fff", borderRadius: 4 }}
            />
            {faqVisible === null && <div className={classes.typing}><span /><span /><span /></div>}
            {faqVisible && !faqVisible.length && <p style={{ fontSize: 13, textAlign: "center" }}>No hay preguntas con esas palabras. Escríbela en el chat.</p>}
            {(faqVisible || []).map((m) => {
              const isOpen = !!faqQuery.trim() || expanded === m.modulo;
              return (
                <div key={m.modulo} className={classes.faqModule}>
                  <button type="button" className={classes.faqHead} aria-expanded={isOpen} onClick={() => setExpanded(isOpen ? null : m.modulo)}>
                    <span>{m.modulo} <small style={{ opacity: 0.6 }}>({m.temas.length})</small></span>
                    <ExpandMoreIcon fontSize="small" style={{ transform: isOpen ? "rotate(180deg)" : "none" }} />
                  </button>
                  {isOpen && m.temas.map((t) => (
                    <button key={t.id} type="button" className={classes.faqItem} onClick={() => ask(t.pregunta)}>
                      <ChevronRightIcon fontSize="small" color="primary" />{t.pregunta}
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </Drawer>
    </>
  );
}
